import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { MONTH_END_INVENTORY } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';

const API = `/api/v1/reports/${MONTH_END_INVENTORY}`;
const LOCATION_OPTIONS = [
  ['all', 'All sellable inventory'],
  ['vegas', 'Vegas warehouse'],
  ['amazon_fba', 'Amazon FBA'],
];
const ACCOUNT_OPTIONS = [
  ['all', 'All inventory accounts'],
  ['11100', '11100 Inventory - Vegas'],
  ['11110', '11110 Inventory - Amazon FBA'],
];
const LOCATION_LABELS = Object.fromEntries(LOCATION_OPTIONS);
const BASIS_LABELS = { weighted: 'Weighted cost', landed: 'Landed cost' };
const COLUMNS = [
  { key: 'location', label: 'Location' },
  { key: 'sku', label: 'SKU' },
  { key: 'category_group', label: 'Category group' },
  { key: 'beginning_units', label: 'Beginning units', type: 'number' },
  { key: 'beginning_unit_cost', label: 'Beginning unit cost', type: 'cost' },
  { key: 'beginning_value', label: 'Beginning value', type: 'currency' },
  { key: 'beginning_basis', label: 'Beginning basis' },
  { key: 'ending_units', label: 'Ending units', type: 'number' },
  { key: 'ending_unit_cost', label: 'Ending unit cost', type: 'cost' },
  { key: 'ending_value', label: 'Ending value', type: 'currency' },
  { key: 'ending_basis', label: 'Ending basis' },
  { key: 'cost_exception', label: 'Cost exception' },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const money = (value, digits = 2) => (value === null || value === undefined ? '—'
  : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: digits }));
const qty = (value) => (value === null || value === undefined ? '—' : Number(value).toLocaleString('en-US'));
const signedMoney = (value) => (value === null || value === undefined ? '—' : `${value > 0 ? '+' : ''}${money(value)}`);
const periodLabel = (period) => {
  const [year, month] = String(period || '').split('-');
  return year && month ? `${month}/${year}` : '';
};
const exceptionLabel = (value) => ({
  no_cost: 'No cost',
  pending_receipt_no_cost: 'No cost (PO not received)',
  no_weighted_cost_used_landed: 'Landed cost used (no weighted cost)',
}[value] || (value ? String(value).replace(/_/g, ' ') : ''));

const formatCell = (row, column) => {
  const value = row[column.key];
  if (column.key === 'location') return LOCATION_LABELS[value] || value;
  if (column.key.endsWith('_basis')) return BASIS_LABELS[value] || value || '—';
  if (column.key === 'cost_exception') return value ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">{exceptionLabel(value)}</span> : '';
  if (column.type === 'number') return qty(value);
  if (column.type === 'currency') return money(value);
  if (column.type === 'cost') return money(value, 4);
  return value ?? '—';
};

const StatusChip = ({ snapshot }) => {
  if (!snapshot) return <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">Not built</span>;
  return snapshot.status === 'LOCKED'
    ? <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Locked at close</span>
    : <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">Rebuilt, not locked</span>;
};

const Card = ({ label, sub, value, detail, tone = '' }) => (
  <div className="rounded bg-gray-50 p-3">
    <div className="text-xs text-gray-500">{label}{sub ? <span className="ml-1 text-gray-400">{sub}</span> : null}</div>
    <div className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</div>
    {detail ? <div className="text-xs text-gray-500 tabular-nums">{detail}</div> : null}
  </div>
);

const pct = (value) => (value === null || value === undefined ? '—' : `${(Number(value) * 100).toFixed(2)}%`);

const RollforwardSection = ({ rollforward, onExport, onExportReceipts }) => {
  if (!rollforward) return null;
  const cell = 'whitespace-nowrap px-3 py-2 tabular-nums';
  return (
    <div className="space-y-3 rounded border bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Rollforward and COGS</h2>
        {rollforward.gate_run ? (
          <span className="text-xs text-gray-500">MEC Gate 2 run #{rollforward.gate_run.id} ({rollforward.gate_run.status}), {new Date(rollforward.gate_run.computed_at).toLocaleString('en-US')}</span>
        ) : null}
        <button type="button" onClick={onExport} className="ml-auto rounded border px-3 py-1 text-xs font-medium hover:bg-gray-50">Export rollforward CSV</button>
        <button type="button" onClick={onExportReceipts} className="rounded border px-3 py-1 text-xs font-medium hover:bg-gray-50">Export receipts CSV</button>
      </div>
      {rollforward.status === 'INCOMPLETE' ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <div className="font-semibold">INCOMPLETE</div>
          <ul className="list-disc px-5">{(rollforward.incomplete_reasons || []).map((reason) => <li key={reason}>{reason}</li>)}</ul>
        </div>
      ) : null}
      {rollforward.package ? (
        <div className="text-xs text-gray-500">
          Package: Month-End Inventory v{rollforward.package.month_end_inventory?.version} ({rollforward.package.month_end_inventory?.status})
          {' · '}{rollforward.package.source_capture ? `source capture #${rollforward.package.source_capture.id} v${rollforward.package.source_capture.version}` : 'no source capture saved'}
          {' · '}{rollforward.package.gate_run ? `Gate 2 run #${rollforward.package.gate_run.id} (${rollforward.package.gate_run.pinned ? `pinned by close snapshot #${rollforward.package.close_snapshot_id}` : 'latest, not pinned'})` : 'no Gate 2 run'}
        </div>
      ) : null}
      <div className="overflow-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-gray-100">
            <tr>{['Location', 'Account', 'Beginning units', 'Beginning value', 'Additions', 'Transfers to Vegas', 'Residual or change', 'Ending units', 'Ending value', 'QuickBooks ending', 'Basis'].map((label) => <th key={label} className="whitespace-nowrap px-3 py-2">{label}</th>)}</tr>
          </thead>
          <tbody>
            {rollforward.locations.map((row) => (
              <tr key={row.key} className="border-t">
                <td className="whitespace-nowrap px-3 py-2 font-medium">{row.label}</td>
                <td className="px-3 py-2">{row.qbo_account}</td>
                {row.available ? (
                  <>
                    <td className={cell}>{qty(row.beginning?.units)}</td>
                    <td className={cell}>{money(row.beginning?.value)}</td>
                    <td className={cell}>{money(row.additions)}</td>
                    <td className={cell}>{money(row.transfers)}</td>
                    <td className={cell}>{money(row.other)}</td>
                    <td className={cell}>{qty(row.ending?.units)}</td>
                    <td className={cell}>{money(row.ending?.value)}</td>
                    <td className={cell}>{money(row.qbo_ending ?? (row.key === 'china' ? row.ending?.value : null))}</td>
                    <td className="px-3 py-2 text-gray-500">{row.other_label ? `${row.other_label}. ` : ''}{row.basis}{row.unexplained ? ` Unexplained ${money(row.unexplained)}.` : ''}{(row.cost_gaps || []).map((gap) => <div key={gap} className="text-red-700">{gap}</div>)}</td>
                  </>
                ) : <td colSpan={9} className="px-3 py-2 text-red-700">Unavailable{row.reason ? `: ${row.reason}` : ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <table className="text-left text-xs">
          <thead className="bg-gray-100"><tr><th className="px-3 py-2" colSpan={2}>COGS bridge (Gate 2)</th></tr></thead>
          <tbody>{rollforward.cogs_bridge.map((line) => <tr key={line.label} className="border-t"><td className="px-3 py-1">{line.label}</td><td className={cell}>{money(line.amount)}</td></tr>)}</tbody>
        </table>
        <table className="text-left text-xs">
          <thead className="bg-gray-100"><tr><th className="px-3 py-2" colSpan={2}>Booked in QuickBooks</th></tr></thead>
          <tbody>{rollforward.booked_cogs.map((line) => <tr key={line.label} className="border-t"><td className="px-3 py-1">{line.acct_num ? `${line.acct_num} ` : ''}{line.label}</td><td className={cell}>{money(line.amount)}</td></tr>)}</tbody>
        </table>
        <div className="space-y-1 text-xs">
          <Card label={rollforward.variance.label} value={signedMoney(rollforward.variance.amount)} detail={`${pct(rollforward.variance.pct_with_reimbursement)} with reimbursement, ${pct(rollforward.variance.pct_without_reimbursement)} without (base: ${rollforward.variance.base})`} tone={rollforward.variance.amount ? 'text-amber-700' : ''} />
          {rollforward.china_to_vegas ? <div className="text-gray-600">China → Vegas receipts: Eadin {money(rollforward.china_to_vegas.eadin_receipts_value)}, booked {money(rollforward.china_to_vegas.booked_transfer_value)} ({rollforward.china_to_vegas.receipt_count} receipts, {rollforward.china_to_vegas.incomplete_receipt_count} incomplete)</div> : null}
        </div>
      </div>
      {rollforward.china_to_vegas?.by_po?.length ? (
        <table className="text-left text-xs">
          <thead className="bg-gray-100"><tr>{['Receipts by PO', 'Receipts', 'Units', 'Value', 'Incomplete'].map((label) => <th key={label} className="px-3 py-2">{label}</th>)}</tr></thead>
          <tbody>{rollforward.china_to_vegas.by_po.map((row) => <tr key={row.po_number} className="border-t"><td className="px-3 py-1">{row.po_number}</td><td className={cell}>{qty(row.receipts)}</td><td className={cell}>{qty(row.units)}</td><td className={cell}>{money(row.value)}</td><td className={cell}>{qty(row.incomplete_receipts)}</td></tr>)}</tbody>
        </table>
      ) : null}
      <table className="text-left text-xs">
        <thead className="bg-gray-100"><tr>{['Tie-out', 'Gate 2', 'Month-End Inventory', 'Difference'].map((label) => <th key={label} className="px-3 py-2">{label}</th>)}</tr></thead>
        <tbody>{rollforward.ties.map((row) => <tr key={row.label} className="border-t"><td className="px-3 py-1">{row.label}</td><td className={cell}>{money(row.gate_2)}</td><td className={cell}>{money(row.month_end_inventory)}</td><td className={cell}>{signedMoney(row.difference)}</td></tr>)}</tbody>
      </table>
      <ul className="list-disc px-5 text-xs text-gray-500">{rollforward.notes.map((note) => <li key={note}>{note}</li>)}</ul>
    </div>
  );
};

const MonthEndInventoryReport = ({ marginFromSidebar, access }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const period = searchParams.get('period') || '';
  const location = searchParams.get('location') || 'all';
  const account = searchParams.get('account') || 'all';
  const sku = searchParams.get('sku') || '';
  const [skuInput, setSkuInput] = useState(sku);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rebuildError, setRebuildError] = useState(null);
  const canEdit = access === 'edit';

  const setFilter = useCallback((key, value, fallback = 'all') => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === fallback) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const query = useCallback(() => {
    const params = new URLSearchParams({ location, account });
    if (period) params.set('period', period);
    if (sku) params.set('sku', sku);
    return params;
  }, [period, location, account, sku]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const { signal, requestId } = beginRequest();
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}?${query()}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (isLatest(requestId)) setReport(json);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => { if (skuInput.trim() !== sku) setFilter('sku', skuInput.trim(), ''); }, 300);
    return () => clearTimeout(timer);
  }, [skuInput, sku, setFilter]);

  const month = report?.months?.find((item) => item.period === report.period);
  const running = month?.rebuild?.state === 'RUNNING';
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => load({ quiet: true }), 5000);
    return () => clearInterval(timer);
  }, [running, load]);

  const rebuild = async () => {
    setRebuildError(null);
    try {
      const response = await fetch(`${API}/${report.period}/rebuild`, { method: 'POST', headers: authHeaders() });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Rebuild failed to start');
      load({ quiet: true });
    } catch (startError) {
      setRebuildError(startError.message);
    }
  };

  const exportData = async () => {
    const response = await fetch(`${API}/export?${query()}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `month-end-inventory-${report?.period || ''}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const exportSection = async (section, name) => {
    const response = await fetch(`${API}/export?period=${report.period}&section=${section}`, { headers: authHeaders() });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}-${report.period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const exportRollforward = () => exportSection('rollforward', 'inventory-rollforward');
  const exportReceipts = () => exportSection('receipts', 'inventory-receipts');

  const summary = report?.summary;
  const totals = report?.totals;
  const rows = report?.data || [];

  const filters = (
    <div className="contents">
      <select aria-label="Location" className="rounded border px-3 py-2 text-sm" value={location} onChange={(event) => setFilter('location', event.target.value)}>
        {LOCATION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <select aria-label="Inventory account" className="rounded border px-3 py-2 text-sm" value={account} onChange={(event) => setFilter('account', event.target.value)}>
        {ACCOUNT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <select aria-label="Month" className="rounded border px-3 py-2 text-sm" value={report?.period || period} onChange={(event) => setFilter('period', event.target.value, '')}>
        {(report?.months || []).map((item) => (
          <option key={item.period} value={item.period}>
            {periodLabel(item.period)}{item.snapshot ? (item.snapshot.status === 'LOCKED' ? ' (locked)' : ' (rebuilt)') : ' (not built)'}
          </option>
        ))}
      </select>
      <input aria-label="SKU" className="rounded border px-3 py-2 text-sm" placeholder="SKU" value={skuInput} onChange={(event) => setSkuInput(event.target.value)} />
    </div>
  );

  return (
    <ReportTemplate
      reportId={MONTH_END_INVENTORY}
      title="Month-End Inventory"
      description="Beginning and ending sellable inventory units and value by month, for the balance sheet."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      access={access}
      isLoading={loading && !report}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={rows} columns={COLUMNS} filename={`month-end-inventory-${report?.period || ''}`} onExport={exportData} disabled={!report?.snapshot} />}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 rounded border bg-white px-4 py-3 text-sm">
          <span className="font-semibold">{periodLabel(report?.period)}</span>
          <StatusChip snapshot={report?.snapshot} />
          {summary ? (
            <span className="text-gray-600">
              {summary.beginning_date} ({BASIS_LABELS[summary.beginning_basis]}) to {summary.ending_date} ({BASIS_LABELS[summary.ending_basis]})
            </span>
          ) : null}
          {report?.snapshot ? <span className="text-xs text-gray-400">Version {report.snapshot.version}, built {new Date(report.snapshot.computed_at).toLocaleString('en-US')}</span> : null}
          {canEdit && report?.period && report?.snapshot?.status !== 'LOCKED' ? (
            <button type="button" onClick={rebuild} disabled={running} className="ml-auto rounded border px-3 py-1 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              {running ? 'Building…' : (report?.snapshot ? 'Rebuild month' : 'Build month')}
            </button>
          ) : null}
        </div>
        {month?.rebuild?.state === 'FAILED' ? <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">Last build failed: {month.rebuild.error}. The saved version below is unchanged.</div> : null}
        {rebuildError ? <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{rebuildError}</div> : null}

        {!report?.snapshot ? (
          <div className="rounded border bg-white px-6 py-12 text-center text-sm text-gray-500">
            {periodLabel(report?.period) || 'This month'} has not been built yet.{canEdit ? ' Use Build month to rebuild it from history.' : ''}
          </div>
        ) : (
          <>
            {summary?.notes?.length ? (
              <ul className="list-disc space-y-0.5 rounded border border-amber-200 bg-amber-50 px-8 py-2 text-xs text-amber-900">
                {summary.notes.map((note) => <li key={note}>{note}</li>)}
              </ul>
            ) : null}
            {totals?.status === 'INCOMPLETE' ? (
              <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
                INCOMPLETE: {(totals.incomplete_reasons || []).join('; ')}. Totals below leave unavailable locations and uncosted units out.
              </div>
            ) : null}
            <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-3 xl:grid-cols-6">
              <Card label="Beginning value" sub={summary.beginning_date} value={money(totals.beginning.value)} detail={`${qty(totals.beginning.units)} units`} />
              <Card label="Ending value" sub={summary.ending_date} value={money(totals.ending.value)} detail={`${qty(totals.ending.units)} units`} />
              <Card label="Change" value={signedMoney(totals.change.value)} detail={`${totals.change.units > 0 ? '+' : ''}${qty(totals.change.units)} units`} />
              <Card label="QuickBooks ending" value={money(totals.qbo?.ending)} detail={totals.qbo ? `Beginning ${money(totals.qbo.beginning)}` : totals.qbo_note} />
              <Card label="Variance (QuickBooks − Eadin)" value={signedMoney(totals.variance?.ending)} detail={totals.variance ? `Beginning ${signedMoney(totals.variance.beginning)}` : ''} tone={totals.variance?.ending ? 'text-amber-700' : ''} />
              <Card label="Missing cost" value={`${qty(totals.missing_cost_units)} units`} detail={`${qty(totals.sku_count)} SKUs in view`} tone={totals.missing_cost_units ? 'text-red-700' : ''} />
            </div>

            <div className="overflow-auto rounded border bg-white">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {['Location', 'QuickBooks account', 'Beginning units', 'Beginning value', 'Ending units', 'Ending value', 'QuickBooks beginning', 'QuickBooks ending', 'Variance beginning', 'Variance ending', 'Left out (not sellable)'].map((label) => (
                      <th key={label} className="whitespace-nowrap px-3 py-2">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.locations.map((item) => (
                    <tr key={item.key} className="border-t">
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{item.label}</td>
                      <td className="whitespace-nowrap px-3 py-2">{item.qbo_account}</td>
                      {item.status === 'UNAVAILABLE' ? (
                        <td colSpan={9} className="px-3 py-2 text-red-700">Unavailable: {item.reason}</td>
                      ) : (
                        <>
                          <td className="px-3 py-2 tabular-nums">{qty(item.beginning.units)}</td>
                          <td className="px-3 py-2 tabular-nums">{money(item.beginning.value)}</td>
                          <td className="px-3 py-2 tabular-nums">{qty(item.ending.units)}</td>
                          <td className="px-3 py-2 tabular-nums">{money(item.ending.value)}</td>
                          <td className="px-3 py-2 tabular-nums">{money(item.qbo.beginning)}</td>
                          <td className="px-3 py-2 tabular-nums">{money(item.qbo.ending)}</td>
                          <td className="px-3 py-2 tabular-nums">{signedMoney(item.variance?.beginning)}</td>
                          <td className="px-3 py-2 tabular-nums">{signedMoney(item.variance?.ending)}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-gray-500">{qty(item.excluded?.sku_count)} SKUs, {qty(item.excluded?.ending_units)} units</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {sku ? <div className="border-t px-3 py-2 text-xs text-gray-500">These are full-account totals. The cards above follow the SKU filter.</div> : null}
            </div>

            <RollforwardSection rollforward={report.rollforward} onExport={exportRollforward} onExportReceipts={exportReceipts} />

            <div className="overflow-auto rounded border bg-white">
              {rows.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">No SKUs match these filters.</div> : (
                <table className="min-w-max text-left text-xs">
                  <thead className="sticky top-0 bg-gray-100"><tr>{COLUMNS.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead>
                  <tbody>{rows.map((row, index) => (
                    <tr key={`${row.location}-${row.sku}`} className={index % 2 ? 'bg-gray-50' : ''}>
                      {COLUMNS.map((column) => <td key={column.key} className={`whitespace-nowrap px-3 py-2 ${column.type ? 'tabular-nums' : ''}`}>{formatCell(row, column)}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div className="text-xs text-gray-500">{report.scope_note} China and in-transit units are added only once received into Vegas. Nothing here posts to QuickBooks.</div>
          </>
        )}
      </div>
    </ReportTemplate>
  );
};

export default MonthEndInventoryReport;
